import * as React from 'react'
import { ArchiveX } from 'lucide-react'

import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '../empty'
import { TablePagination, TablePaginationProps } from './pagination'

import { cn } from '@/lib/utils'

// import { TablePagination, TablePaginationProps } from './pagination';

const TableElement = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  ),
)
TableElement.displayName = 'Table'

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />,
)
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
  ),
)
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)} {...props} />
  ),
)
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn('border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted', className)}
      {...props}
    />
  ),
)
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  ),
)
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]', className)}
      {...props}
    />
  ),
)
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
  ),
)
TableCaption.displayName = 'TableCaption'

export { TableBody, TableCaption, TableCell, TableElement, TableFooter, TableHead, TableHeader, TableRow }

export type TableColumn<Entry> = {
  title: string
  field: keyof Entry
  Header?(): React.ReactElement
  Cell?({ entry }: { entry: Entry }): React.ReactElement
  CustomCell?({ entry }: { entry: Entry }): React.ReactElement
}

export type TableProps<Entry> = {
  data: Entry[]
  columns: TableColumn<Entry>[]
  onRowClick?(e: React.MouseEvent, entry: Entry): void
  pagination?: TablePaginationProps
}

export const Table = <Entry extends { id: string }>({ data, columns, onRowClick, pagination }: TableProps<Entry>) => {
  if (!data?.length) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <ArchiveX />
          </EmptyMedia>
          <EmptyTitle>Aucune donnée disponible</EmptyTitle>
          <EmptyDescription>Il n'y a actuellement aucune donnée à afficher ici.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <>
      <TableElement>
        <TableHeader>
          <TableRow>
            {columns.map(({ Header, title }, index) =>
              Header ? <Header key={title + index} /> : <TableHead key={title + index}>{title}</TableHead>,
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((entry, entryIndex) => (
            <TableRow
              className={onRowClick ? 'cursor-pointer' : ''}
              key={entry?.id || entryIndex}
              onClick={(e) => onRowClick?.(e, entry)}
            >
              {columns.map(({ Cell, CustomCell, field, title }, columnIndex) =>
                CustomCell ? (
                  <CustomCell key={title + columnIndex} entry={entry} />
                ) : (
                  <TableCell key={title + columnIndex}>{Cell ? <Cell entry={entry} /> : `${entry[field]}`}</TableCell>
                ),
              )}
            </TableRow>
          ))}
        </TableBody>
      </TableElement>

      {pagination && <TablePagination {...pagination} />}
    </>
  )
}
